import { useState, useCallback, useMemo, useRef } from 'react';
import { EntityType } from '../../../types/workflow';
import { workflowService } from '../../../services/workflowService';
import { EntityOption } from '../EntityOptionItem';

/**
 * Simple debounce utility function to avoid lodash dependency.
 */
function debounce<T extends (...args: Parameters<T>) => void>(
  fn: T,
  delay: number
): T & { cancel: () => void } {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const debounced = (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      fn(...args);
      timeoutId = null;
    }, delay);
  };

  debounced.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return debounced as T & { cancel: () => void };
}

interface UseEntitySearchProps {
  allowedEntityTypes?: EntityType[];
}

interface UseEntitySearchReturn {
  open: boolean;
  inputValue: string;
  setInputValue: (value: string) => void;
  options: EntityOption[];
  loading: boolean;
  handleOpen: () => void;
  handleClose: () => void;
  fetchEntities: (searchTerm: string) => Promise<void>;
  debouncedFetch: (term: string) => void;
}

/**
 * Custom hook for searching entities (Farmers, Agents, Labs) via the
 * consolidated /api/workflow/entities/search endpoint.
 *
 * Follows a hybrid filtering pattern:
 * - "Load on open": preloads all entities when the Autocomplete opens
 * - "Search as you type": debounced server-side search for background refresh
 * - Client-side filtering via MUI createFilterOptions provides instant results
 */
export function useEntitySearch({
  allowedEntityTypes,
}: UseEntitySearchProps): UseEntitySearchReturn {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<EntityOption[]>([]);
  const [loading, setLoading] = useState(false);
  const hasLoadedOnce = useRef(false);

  // Stable entity types array for the API call
  const entityTypesToSearch = useMemo(() => {
    return allowedEntityTypes || (['Farmer', 'Agent', 'Lab'] as EntityType[]);
  }, [allowedEntityTypes]);

  // Fetch entities via consolidated endpoint
  const fetchEntities = useCallback(
    async (searchTerm: string) => {
      setLoading(true);
      try {
        const results = await workflowService.searchEntities({
          searchTerm: searchTerm || undefined,
          entityTypes: entityTypesToSearch,
          pageSize: 200,
        });

        const mapped: EntityOption[] = results.map((r) => ({
          entityType: r.entityType as EntityType,
          entityId: r.entityId,
          entityName: r.entityName,
          email: r.email,
          isSageReady: r.isSageReady,
          hasSageSalesRepLink: r.hasSageSalesRepLink,
          secondaryText: r.secondaryText,
        }));

        setOptions(mapped);
      } catch {
        // Search failure silently handled - options remain empty
      } finally {
        setLoading(false);
      }
    },
    [entityTypesToSearch]
  );

  // Debounced search for background refresh while typing
  const debouncedFetch = useMemo(
    () => debounce((term: string) => fetchEntities(term), 300),
    [fetchEntities]
  );

  // Load options when Autocomplete opens (first time only)
  const handleOpen = useCallback(() => {
    setOpen(true);
    if (!hasLoadedOnce.current) {
      hasLoadedOnce.current = true;
      fetchEntities('');
    }
  }, [fetchEntities]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  return {
    open,
    inputValue,
    setInputValue,
    options,
    loading,
    handleOpen,
    handleClose,
    fetchEntities,
    debouncedFetch,
  };
}

export default useEntitySearch;
